import { CheckCircle, XCircle, Clock, UserCheck, Truck, AlertCircle, FileCheck, Briefcase } from 'lucide-react';
import { useState } from 'react';
import translations from '../../../../translations/PurchaseInvoiceTranslation';
import { translateStatus } from '../../../../Utils/purchaseInvoices/statusTranslations';

const STATUS_MAP = {
  approved: {
    icon: <CheckCircle size={18} style={{ color: '#10b981' }} />,
    text: 'החשבונית אושרה בהצלחה',
    style: 'fileItemApproved'
  },
  pendingApproval: {
    icon: <UserCheck size={18} style={{ color: '#f59e0b' }} />,
    text: 'ממתין לאישור',
    style: 'fileItemPending'
  },
  pendingGoods: {
    icon: <Truck size={18} style={{ color: '#f59e0b' }} />,
    text: 'ממתין לקבלת סחורה',
    style: 'fileItemPending'
  },
  retry: {
    icon: <Clock size={18} style={{ color: '#3b82f6' }} />,
    text: 'בתהליך עיבוד חוזר',
    style: 'fileItemProcessing'
  },
  processing: {
    icon: <Clock size={18} style={{ color: '#3b82f6' }} />,
    text: 'החשבונית הועלתה בהצלחה ונמצאת בתהליך עיבוד',
    style: 'fileItemProcessing'
  },
  error: {
    icon: <AlertCircle size={18} style={{ color: '#ef4444' }} />,
    text: 'שגיאה',
    style: 'fileItemRejected'
  },
  rejected: {
    icon: <XCircle size={18} style={{ color: '#ef4444' }} />,
    text: 'נדחה',
    style: 'fileItemRejected'
  },
  retainer: {
    icon: <Briefcase size={18} style={{ color: '#8b5cf6' }} />,
    text: 'חשבונית ריטיינר - ממתין לשיוך הזמנות',
    style: 'fileItemPending'
  },
  requiresAttention: {
    icon: <AlertCircle size={18} style={{ color: '#ea580c' }} />,
    text: 'חשבונית להמשך טיפול',
    style: 'fileItemPending'
  }
};

const translateErrors = (err) => {
  if (err === 'duplicate_check') {
    return 'חשבונית קיימת במאגר';
  } else if (typeof err === 'string' && err.includes('SupplierInvoices validation failed')) {
    return 'שגיאה בשמירת חשבונית';
  } else {
    return err || 'שגיאה לא ידועה';
  }
};

const getFileStatus = (file) => {
  const status = (file.status || '').toLowerCase();
  const fullMessage = (typeof file.fullMessage === 'string' ? file.fullMessage : file.message) || '';


  if (status === 'approved' || status === 'מאושר') {
    return 'approved';
  }
  if (status === 'rejected' || status === 'נדחה') {
    return 'rejected';
  }
  

  if (status === 'pending_retainer_allocation' || 
      status === 'חשבונית ריטיינר - ממתין לשיוך הזמנות' ||
      file.isRetainerInvoice) {
    return 'retainer';
  }
  

  if (status === 'requires_attention' || 
      status === 'חשבונית להמשך טיפול' ||
      file.errorDetails) {
    return 'requiresAttention';
  }
  

  if (status === 'pending_goods_receipt' || 
      status === 'ממתין לקבלת סחורה' ||
      fullMessage.includes('goods receipt')) {
    return 'pendingGoods';
  }
  
  if (status === 'pending_approval' || 
      status === 'pendingapproval' || 
      status === 'pending' ||
      status === 'ממתין לאישור') {
    return 'pendingApproval';
  }
  
  if (status === 'processing' || status === 'בעיבוד') {
    return 'processing';
  }
  
  if (file?.message?.details?.retry) return 'retry';
  if (status === 'error' || status === 'approval_signature_failed') return 'error';
  if (status === 'success') return 'pendingApproval';
  
  return 'error';
};

const UploadedFilesList = ({ files, styles }) => {
  const [tooltipVisible, setTooltipVisible] = useState(null);

  if (!files || files.length === 0) return null;

  return (
    <div style={styles.uploadedSection}>
      <style>
        {`
          @keyframes fadeInUp {
            from { 
              opacity: 0; 
              transform: translateY(10px); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0); 
            }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateX(20px); 
            }
            to { 
              opacity: 1; 
              transform: translateX(0); 
            }
          }
        `}
      </style>

      <div style={{ 
        fontSize: '1rem', 
        fontWeight: '600', 
        color: '#334155', 
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        animation: 'fadeInUp 0.5s ease-out'
      }}>
        <FileCheck size={18} style={{ color: '#10b981' }} />
        {translations.successfullyUploadedFiles || 'קבצים שהועלו בהצלחה'}
        <span style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '10px',
          marginRight: '4px'
        }}>
          {files.length}
        </span>
      </div>
      
      <div style={styles.fileList}>
        {files.map((file, index) => {
          const fileStatus = getFileStatus(file);
          const statusObj = STATUS_MAP[fileStatus] || STATUS_MAP.error;
          let statusMessage = statusObj.text;
          if (fileStatus === 'error' || fileStatus === 'rejected') {
            statusMessage = translateErrors(file.message?.stage || file.message);
          }
          return (
            <div 
              key={index} 
              style={{
                ...styles.fileItemContainer,
                animation: 'slideIn 0.5s ease-out',
                animationDelay: `${index * 0.1}s`,
                animationFillMode: 'backwards'
              }}
            >
              {tooltipVisible === index && (
                <div style={{
                  ...styles.tooltip,
                  animation: 'fadeInUp 0.3s ease-out'
                }}>
                  {statusMessage}
                </div>
              )}
              <div
                style={{ 
                  ...styles.fileItem, 
                  ...(styles[statusObj.style] || {}),
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  setTooltipVisible(index);
                  e.currentTarget.style.transform = 'translateX(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  setTooltipVisible(null);
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <span style={styles.fileName}>
                  {statusObj.icon}
                  <span style={styles.fileNameText}>{file.fileName || file.name}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UploadedFilesList;